import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const eur = (v, locale) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(v || 0);

// Client space: extra employees (10 € HT/month each, max 50 per business), prorated on the running subscription.
export const EmployeesCard = () => {
  const { t, locale } = useI18n();
  const fmtMoney = (v) => eur(v, locale);
  const [s, setS] = useState(null);
  const [qty, setQty] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/dev/billing/employees").then(({ data }) => { setS(data); setQty(data.paid); }).catch(() => {});
  }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/dev/billing/employees", { quantity: qty });
      setS(data);
      toast.success(t("emp.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const free = s.offered + s.group + s.vip;
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="employees-card">
      <h2 className="flex min-w-0 items-center gap-2 break-words font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Users className="h-5 w-5 shrink-0" /> {t("emp.title")}</h2>
      <p className="break-words text-sm text-neutral-300">{t("emp.intro", { n: s.field_included, price: fmtMoney(s.price_ht_eur, "eur"), max: s.max })}</p>
      <p className="break-words text-sm font-semibold text-white" data-testid="employees-total">{t("emp.total", { n: s.total })}{free ? ` · ${t("emp.free", { n: free })}` : ""}{s.offer_paid ? ` · ${t("emp.inOffer", { n: s.offer_paid })}` : ""}</p>
      {s.can_buy ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <select value={qty} onChange={(e) => setQty(Number(e.target.value))} aria-label={t("emp.choose")} className="rounded-md border border-neutral-700 bg-black/50 px-3 py-2 text-sm text-white" data-testid="employees-select">
            {Array.from({ length: s.max_self + 1 }, (_, i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <span className="break-words text-xs text-neutral-400" data-testid="employees-price">{t("emp.price", { price: fmtMoney(qty * s.price_ht_eur, "eur") })}</span>
          <button type="button" className="btn-gold btn-sm" disabled={busy || qty === s.paid} onClick={save} data-testid="employees-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("emp.save")}</button>
        </div>
      ) : (
        <p className="break-words text-xs text-neutral-400" data-testid="employees-locked">{t(s.reason === "vip" ? "emp.vip" : "emp.needSub")}{" "}
          {s.reason !== "vip" && <Link to="/espace-developpeur/messages?nouveau=custom" className="text-[var(--gold-bright)] underline" data-testid="employees-ask">{t("emp.ask")}</Link>}</p>
      )}
      <p className="break-words text-[0.7rem] text-neutral-500">{t("emp.prorated")}</p>
    </section>
  );
};
